import React, { useEffect, useState } from "react";
import { Col, Row, Toast, ToastContainer } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

import SearchWithAPI from "../common/search-box-new";
import Loader from "../common/Loader.jsx";
import { sanitizeInput } from "../common/security-utils";
import {
  addOpportunityAttachmentApi,
  createOpportunityApi,
  deleteOpportunityAttachmentApi,
  getOpportunityEditApi,
  OPPORTUNITY_STAGES,
  updateOpportunityApi,
} from "./opportunity-api";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";

const apiUrl = process.env.REACT_APP_API_URL;
const apiAccUrl = process.env.REACT_APP_ADMIN_API_URL;

const emptyDiscussion = () => ({ id: "", discussion_date: "", comment: "" });

const OpportunityCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const opportunityId = location.state?.id;

  const [loading, setLoading] = useState(!!opportunityId);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState({});
  const [toast, setToast] = useState({ show: false, message: "", success: true });

  const [type, setType] = useState("Customer");
  const [party, setParty] = useState(null); // customer or lead, depending on type
  // Address mirrors Leads: ids from the admin lookups, not free text, so the
  // two modules can be filtered and reported on the same way.
  const [cityOption, setCityOption] = useState(null);
  const [stateOption, setStateOption] = useState(null);
  const [countryOption, setCountryOption] = useState(null);

  // An option carries TWO ids: `id` is the admin service's own uuid, and
  // city_id/state_id/country_id are the integers that match this database's
  // cities/states/countries tables. Only the integers are usable here -
  // leads-create reads option.state_id and option.country_id for the same
  // reason. Taking option.id instead stores a uuid that joins to nothing.
  //
  // Picking a city fills in its state and country; picking a state clears a
  // city that may no longer belong to it.
  const handleCityChange = (option) => {
    setCityOption({ label: option.label, value: option.city_id });
    setStateOption({ label: option.state_name, value: option.state_id });
    setCountryOption({ label: option.country_name, value: option.country_id });
  };

  const handleStateChange = (option) => {
    setStateOption({ label: option.label, value: option.state_id });
    setCountryOption({ label: option.country_name, value: option.country_id });
    setCityOption(null);
  };

  const handleCountryChange = (option) => {
    setCountryOption({ label: option.label, value: option.country_id });
    setStateOption(null);
    setCityOption(null);
  };
  const [salesPerson, setSalesPerson] = useState(null);
  const [currency, setCurrency] = useState(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    lead_source: "",
    opportunity_date: new Date().toISOString().slice(0, 10),
    stage: "New",
    probability: 0,
    expected_close_date: "",
    amount: 0,
    address: "",
    city_id: "",
    state_id: "",
    country_id: "",
    zip_code: "",
  });

  const [discussions, setDiscussions] = useState([emptyDiscussion()]);
  const [removedDiscussions, setRemovedDiscussions] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!opportunityId) return;
    (async () => {
      try {
        const response = await getOpportunityEditApi(opportunityId);
        const data = response?.data?.data;
        if (!data) return;

        setType(data.type || "Customer");
        const linked = data.type === "Lead" ? data.lead : data.customer;
        if (linked?.value) setParty(linked);
        if (data.sales_person?.value) setSalesPerson(data.sales_person);
        // Built from the flat id+name pair the same way leads-create does,
        // rather than expecting a ready-made object from the server.
        if (data.city_id) setCityOption({ label: data.city ?? "", value: data.city_id });
        if (data.state_id) setStateOption({ label: data.state ?? "", value: data.state_id });
        if (data.country_id) setCountryOption({ label: data.country ?? "", value: data.country_id });
        if (data.currency?.value) setCurrency(data.currency);

        setForm({
          title: data.title ?? "",
          description: data.description ?? "",
          lead_source: data.lead_source ?? "",
          opportunity_date: data.opportunity_date ?? "",
          stage: data.stage ?? "New",
          probability: data.probability ?? 0,
          expected_close_date: data.expected_close_date ?? "",
          amount: data.amount ?? 0,
          address: data.address ?? "",
          city_id: data.city_id ?? "",
          state_id: data.state_id ?? "",
          country_id: data.country_id ?? "",
          zip_code: data.zip_code ?? "",
        });

        setDiscussions(data.discussions?.length ? data.discussions : [emptyDiscussion()]);
        setAttachments(data.attachments ?? []);
      } catch (error) {
        console.error("Error loading opportunity:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [opportunityId]);

  const showToast = (message, success) => setToast({ show: true, message, success });

  const handleSubmit = async () => {
    setSaving(true);
    setErrorMsg({});
    try {
      const payload = {
        type,
        lead_id: type === "Lead" ? party?.value ?? null : null,
        customer_id: type === "Customer" ? party?.value ?? null : null,
        title: sanitizeInput(form.title),
        description: sanitizeInput(form.description),
        sales_person_id: salesPerson?.value ?? null,
        lead_source: sanitizeInput(form.lead_source),
        opportunity_date: form.opportunity_date,
        stage: form.stage,
        probability: Number(form.probability) || 0,
        expected_close_date: form.expected_close_date || null,
        amount: Number(form.amount) || 0,
        currency_id: currency?.value ?? null,
        address: sanitizeInput(form.address),
        // || not ??, matching leads-create: ?? only replaces null and
        // undefined, so an empty-string value reaches the server and fails
        // the integer rule. || turns "" and 0 into null as well.
        city_id: cityOption?.value || null,
        state_id: stateOption?.value || null,
        country_id: countryOption?.value || null,
        zip_code: sanitizeInput(form.zip_code),
        discussions: discussions.filter((row) => row.comment || row.discussion_date),
        removed_discussions: removedDiscussions,
      };

      const response = opportunityId
        ? await updateOpportunityApi(opportunityId, payload)
        : await createOpportunityApi(payload);

      if (response?.status === 400) {
        const errors = {};
        Object.entries(response.data?.error ?? {}).forEach(([key, value]) => {
          errors[key] = Array.isArray(value) ? value[0] : value;
        });
        setErrorMsg(errors);
        showToast(response.data?.message ?? "Validation error", false);
        return;
      }

      showToast(response?.data?.message ?? "Saved", response?.data?.success);
      if (response?.data?.success) setTimeout(() => navigate("/opportunity/list"), 600);
    } catch (error) {
      console.error("Error saving opportunity:", error);
      showToast("Unable to save opportunity", false);
    } finally {
      setSaving(false);
    }
  };

  // Attachments are pushed straight away on an existing record; on a new one
  // the record has to be saved first (there's no id to attach to yet).
  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!opportunityId) {
      showToast("Save the opportunity first, then add attachments.", false);
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const response = await addOpportunityAttachmentApi(opportunityId, reader.result);
      showToast(response?.data?.message ?? "Uploaded", response?.data?.success);
      if (response?.data?.success) {
        const refreshed = await getOpportunityEditApi(opportunityId);
        setAttachments(refreshed?.data?.data?.attachments ?? []);
      }
    };
    reader.readAsDataURL(file);
  };

  const removeAttachment = async (id) => {
    const response = await deleteOpportunityAttachmentApi(id);
    if (response?.data?.success) setAttachments((prev) => prev.filter((item) => item.id !== id));
  };

  const removeDiscussion = (index) => {
    setDiscussions((prev) => {
      const row = prev[index];
      if (row?.id) setRemovedDiscussions((ids) => [...ids, row.id]);
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [emptyDiscussion()];
    });
  };

  const updateDiscussion = (index, key, value) =>
    setDiscussions((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));

  if (loading) return <Loader height="80vh" />;

  const field = (label, key, props = {}) => (
    <div className="mb-3">
      <label className="fs-14 fw-500 text-grey mb-1">{label}</label>
      <input
        className="form-control"
        value={form[key]}
        onChange={(e) => setField(key, e.target.value)}
        {...props}
      />
      {errorMsg[key] && <span className="fs-12" style={{ color: "#ff4444" }}>{errorMsg[key]}</span>}
    </div>
  );

  return (
    <div style={{ padding: "16px" }}>
      <ToastContainer position="top-end">
        <Toast bg={toast.success ? "success" : "danger"} show={toast.show} onClose={() => setToast({ ...toast, show: false })} delay={3000} autohide>
          <Toast.Body style={{ color: "#ffffff" }}>{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="fs-20 fw-600 text-dkblue mb-3">{opportunityId ? "Edit" : "Create"} Opportunity</div>

      <div className="q-customer-card p-4 mb-3" style={{ background: "#fff", borderRadius: "16px" }}>
        <div className="fs-16 fw-600 text-dkblue mb-3">Customer Info</div>
        <Row>
          <Col md={6}>
            <div className="mb-3">
              <label className="fs-14 fw-500 text-grey mb-1">Type</label>
              <select
                className="form-control"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setParty(null);
                }}
              >
                <option value="Customer">Customer</option>
                <option value="Lead">Lead</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="fs-14 fw-500 text-grey mb-1">{type} *</label>
              <SearchWithAPI
                onDropdownChange={setParty}
                apiUrl={apiUrl + (type === "Lead" ? "get_leads" : "get_customers")}
                placeholder="Select"
                selectedOption={party}
              />
              {(errorMsg.customer_id || errorMsg.lead_id) && (
                <span className="fs-12" style={{ color: "#ff4444" }}>{errorMsg.customer_id || errorMsg.lead_id}</span>
              )}
            </div>

            {field("Project Name *", "title", { placeholder: "Enter here..." })}
          </Col>

          <Col md={6}>
            <div className="mb-3">
              <label className="fs-14 fw-500 text-grey mb-1">Sales Person</label>
              <SearchWithAPI onDropdownChange={setSalesPerson} apiUrl={apiUrl + "get_lead_owner"} placeholder="Select" selectedOption={salesPerson} />
            </div>

            {field("Source of Lead", "lead_source", { placeholder: "Enter here..." })}

            <div className="mb-3">
              <label className="fs-14 fw-500 text-grey mb-1">Project Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="Enter project description here..."
              />
            </div>
          </Col>
        </Row>
      </div>

      <Row>
        <Col md={4}>
          <div className="q-customer-card p-4 mb-3" style={{ background: "#fff", borderRadius: "16px", height: "100%" }}>
            <div className="fs-16 fw-600 text-dkblue mb-3">Opportunity Overview</div>
            {field("Opportunity Date *", "opportunity_date", { type: "date" })}
            {errorMsg.opportunity_date && <span className="fs-12" style={{ color: "#ff4444" }}>{errorMsg.opportunity_date}</span>}

            <div className="mb-3">
              <label className="fs-14 fw-500 text-grey mb-1">Stage</label>
              <select className="form-control" value={form.stage} onChange={(e) => setField("stage", e.target.value)}>
                {OPPORTUNITY_STAGES.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </div>

            {field("Probability (%)", "probability", { type: "number", min: 0, max: 100 })}
          </div>
        </Col>

        <Col md={4}>
          <div className="q-customer-card p-4 mb-3" style={{ background: "#fff", borderRadius: "16px", height: "100%" }}>
            <div className="fs-16 fw-600 text-dkblue mb-3">Business Details</div>
            {field("Expected Value", "amount", { type: "number", min: 0 })}

            <div className="mb-3">
              <label className="fs-14 fw-500 text-grey mb-1">Currency</label>
              <SearchWithAPI onDropdownChange={setCurrency} apiUrl={apiUrl + "get_currency"} placeholder="Select" selectedOption={currency} />
            </div>

            {field("Expected Close Date", "expected_close_date", { type: "date" })}
          </div>
        </Col>

        <Col md={4}>
          <div className="q-customer-card p-4 mb-3" style={{ background: "#fff", borderRadius: "16px", height: "100%" }}>
            <div className="fs-16 fw-600 text-dkblue mb-3">Address Details</div>
            {field("Address", "address", { placeholder: "Ex:XYZ Street" })}

            <div className="mb-3">
              <label className="fs-14 fw-500 text-grey mb-1">Country</label>
              <SearchWithAPI
                onDropdownChange={handleCountryChange}
                apiUrl={apiAccUrl + "get_country"}
                placeholder="Select"
                selectedOption={countryOption}
              />
            </div>

            <div className="mb-3">
              <label className="fs-14 fw-500 text-grey mb-1">State</label>
              <SearchWithAPI
                onDropdownChange={handleStateChange}
                apiUrl={apiAccUrl + "get_state"}
                params={{ country_id: countryOption?.value ?? "" }}
                placeholder="Select"
                selectedOption={stateOption}
              />
            </div>

            <div className="mb-3">
              <label className="fs-14 fw-500 text-grey mb-1">City</label>
              <SearchWithAPI
                onDropdownChange={handleCityChange}
                apiUrl={apiAccUrl + "get_city"}
                params={{ state_id: stateOption?.value ?? "" }}
                placeholder="Select"
                selectedOption={cityOption}
              />
            </div>

            {field("Postal Code", "zip_code", { placeholder: "Enter here..." })}
          </div>
        </Col>
      </Row>

      <div className="q-customer-card p-4 mb-3" style={{ background: "#fff", borderRadius: "16px" }}>
        <div className="fs-16 fw-600 text-dkblue mb-3">Discussion</div>
        {discussions.map((row, index) => (
          <Row key={index} className="align-items-end mb-2">
            <Col md={3}>
              <label className="fs-12 fw-500 text-grey mb-1">DATE</label>
              <input
                type="date"
                className="form-control"
                value={row.discussion_date || ""}
                onChange={(e) => updateDiscussion(index, "discussion_date", e.target.value)}
              />
            </Col>
            <Col md={8}>
              <label className="fs-12 fw-500 text-grey mb-1">DISCUSSION COMMENT</label>
              <textarea
                className="form-control"
                rows={1}
                value={row.comment || ""}
                onChange={(e) => updateDiscussion(index, "comment", e.target.value)}
                placeholder="Enter here..."
              />
            </Col>
            <Col md={1}>
              <DeleteRow style={{ cursor: "pointer" }} onClick={() => removeDiscussion(index)} />
            </Col>
          </Row>
        ))}
        <button
          className="fs-14 fw-600"
          style={{ border: "none", background: "#E5F6FF", color: "#3D4AF5", borderRadius: "100px", padding: "8px 18px" }}
          onClick={() => setDiscussions((prev) => [...prev, emptyDiscussion()])}
        >
          + Add new
        </button>
      </div>

      <div className="q-customer-card p-4 mb-3" style={{ background: "#fff", borderRadius: "16px" }}>
        <div className="fs-16 fw-600 text-dkblue mb-3">Attachment</div>
        <label
          style={{
            display: "block",
            border: "1px dashed #b9b9b9",
            borderRadius: "8px",
            padding: "28px",
            textAlign: "center",
            cursor: "pointer",
          }}
        >
          <input type="file" style={{ display: "none" }} onChange={handleFile} />
          <span className="fs-14 fw-500" style={{ color: "#3D4AF5" }}>+ Click here to upload a file</span>
        </label>

        {attachments.map((file) => (
          <div key={file.id} className="d-flex align-items-center justify-content-between mt-2">
            <a className="fs-14 fw-500 textblue" href={file.url} target="_blank" rel="noreferrer">{file.file_name}</a>
            <DeleteRow style={{ cursor: "pointer" }} onClick={() => removeAttachment(file.id)} />
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-end gap-2">
        <button className="btn-cancel" onClick={() => navigate(-1)}>Back</button>
        <button className="btn-save" disabled={saving} onClick={handleSubmit}>
          {saving ? "Saving..." : "Submit"}
        </button>
      </div>
    </div>
  );
};

export default OpportunityCreate;
